import './styles.scss';

import { useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '@components/CustomModal';
import { Text, Input, ButtonGroup } from '@fragments';
import { IconFileUpload, IconAlertCircle, IconSearch } from '@tabler/icons-react';

import useTemplatesModal from '../hooks/useTemplatesModal';
import TemplateCard from './components/TemplateCard';
import getTemplates from '../templates';

const ALL_FILTER = 'all';

export default function TemplatesModalView({ open, onClose, onSelectTemplate }) {
    const { t } = useTranslation('formsModals');
    const fileInputRef = useRef(null);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(ALL_FILTER);

    const templates = useMemo(() => getTemplates(t), [t]);

    const filterOptions = useMemo(() => [
        { label: t('templates.filters.all'), value: ALL_FILTER },
        ...templates
            .map((template) => ({ label: template.indicatedFor, value: template.indicatedFor }))
            .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i),
    ], [templates, t]);

    const {
        dragOver,
        error,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleFileInput,
        clearError,
    } = useTemplatesModal({ onSelectTemplate });

    const filteredTemplates = useMemo(() => templates.filter((template) => {
        const q = search.trim().toLowerCase();
        const matchesSearch = !q
            || template.title.toLowerCase().includes(q)
            || template.description.toLowerCase().includes(q);
        const matchesFilter = activeFilter === ALL_FILTER || template.indicatedFor === activeFilter;
        return matchesSearch && matchesFilter;
    }), [templates, search, activeFilter]);

    return (
        <CustomModal
            open={open}
            handleClose={onClose}
            maxWidth={1160}
            hideSecondaryButton
            title={t('templates.modal.title')}
            bodyComponent={
                <div className="templates-modal-body">
                    {/* ── File import ────────────────────────────────── */}
                    <div className="tm-section">
                        <Text.h5 weight={400} contrast={7}>{t('templates.import.title')}</Text.h5>

                        <div
                            className={[
                                'tm-dropzone',
                                dragOver && 'tm-dropzone--active',
                                error && 'tm-dropzone--error',
                            ].filter(Boolean).join(' ')}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => { clearError(); fileInputRef.current?.click(); }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".json,application/json"
                                style={{ display: 'none' }}
                                onChange={handleFileInput}
                            />

                            <div className="tm-dz-icon">
                                {error ? <IconAlertCircle size={24} /> : <IconFileUpload size={24} />}
                            </div>

                            <div className="tm-dz-text">
                                {error ? (
                                    <>
                                        <Text.span contrast={8} weight={500} className="tm-dz-error-title">
                                            {t('templates.import.errorTitle')}
                                        </Text.span>
                                        <Text.span contrast={7} className="tm-dz-hint">{error}</Text.span>
                                        <Text.span contrast={4} className="tm-dz-hint">
                                            {t('templates.import.retryHint')}
                                        </Text.span>
                                    </>
                                ) : (
                                    <>
                                        <Text.span contrast={7}>
                                            {dragOver
                                                ? t('templates.import.dropHere')
                                                : t('templates.import.dropzone')}
                                        </Text.span>
                                        <Text.span contrast={4} className="tm-dz-hint">
                                            {t('templates.import.hint')}
                                        </Text.span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* ── Gallery ────────────────────────────────────── */}
                    <div className="tm-section">
                        <Text.h5 weight={400} contrast={7}>{t('templates.gallery.title')}</Text.h5>

                        <div className="tm-sticky-header">
                            <Input.Text
                                value={search}
                                onInput={setSearch}
                                placeholder={t('templates.gallery.searchPlaceholder')}
                                start={<IconSearch />}
                                fullWidth
                                autoFocus
                            />

                            <div className="tm-filters">
                                <ButtonGroup
                                    options={filterOptions}
                                    value={activeFilter}
                                    onChange={setActiveFilter}
                                    size="small"
                                />
                            </div>
                        </div>

                        {filteredTemplates.length > 0 ? (
                            <div className="tm-gallery">
                                {filteredTemplates.map((template) => (
                                    <TemplateCard
                                        key={template.id}
                                        template={template}
                                        onClick={() => onSelectTemplate?.(template.formData)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="tm-empty">
                                <Text.span contrast={4}>{t('templates.gallery.empty')}</Text.span>
                            </div>
                        )}
                    </div>
                </div>
            }
        />
    );
}
